import "./BundleCarousel.css";

import {
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { useAuth } from "../Context/AuthContext";
import { API_BASE } from "../Services/Api";

// const API_BASE_URL = (
//   process.env.REACT_APP_API_BASE_URL ||
//   "https://lsc-india.org/backend-edc/public"
// ).replace(/\/$/, "");

const GROUP_ORDER = [
  "BUNDLE1-A",
  "BUNDLE1-B",
  "BUNDLE3",
  "BUNDLE2",
];

const GROUP_LABELS = {
  "BUNDLE1-A": "Warehousing Goods Handling",
  "BUNDLE1-B": "Warehouse Management",
  BUNDLE2: "LSC Brand New E-Courses",
  BUNDLE3: "Logistics Operations",
};

export default function BundleCarousel({
  isOpen,
  onClose,
}) {
  const { user } = useAuth();

  const formRef = useRef(null);

  const [bundles, setBundles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] =
    useState("");
  const [reloadKey, setReloadKey] = useState(0);
  const [payOption, setPayOption] =
    useState(null);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const controller = new AbortController();

    const loadBundles = async () => {
      try {
        setLoading(true);
        setErrorMessage("");

        const response = await fetch(
          `${API_BASE}/course-bundles`,
          {
            method: "GET",
            headers: {
              Accept: "application/json",
            },
            signal: controller.signal,
          }
        );

        if (!response.ok) {
          throw new Error(
            `HTTP error: ${response.status}`
          );
        }

        const result = await response.json();

        if (
          result.success !== true ||
          !Array.isArray(result.data)
        ) {
          throw new Error(
            result.message ||
              "Invalid bundle API response."
          );
        }

        setBundles(result.data);
      } catch (error) {
        if (error.name !== "AbortError") {
          console.error(
            "Unable to fetch bundles:",
            error
          );

          setErrorMessage(
            "Unable to load course bundles. Please check the bundle API."
          );
        }
      } finally {
        setLoading(false);
      }
    };

    loadBundles();

    return () => {
      controller.abort();
    };
  }, [isOpen, reloadKey]);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const previousOverflow =
      document.body.style.overflow;

    document.body.style.overflow = "hidden";

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener(
      "keydown",
      handleEscape
    );

    return () => {
      document.body.style.overflow =
        previousOverflow;

      document.removeEventListener(
        "keydown",
        handleEscape
      );
    };
  }, [isOpen, onClose]);

  const data = useMemo(() => {
    const groupMap = new Map();

    bundles.forEach((bundle) => {
      const courses = Array.isArray(
        bundle.courses
      )
        ? bundle.courses
        : [];

      courses.forEach((course) => {
        const code =
          course.group_code ||
          `GROUP-${course.group_id}`;

        if (!groupMap.has(code)) {
          groupMap.set(code, {
            type: "individual",
            code,
            name:
              GROUP_LABELS[code] ||
              course.group_name ||
              "Individual Bundle",
            price: Number(
              course.single_price || 0
            ),
            courses: [],
          });
        }

        const group = groupMap.get(code);

        const alreadyAdded =
          group.courses.some(
            (item) =>
              Number(item.id) ===
              Number(course.id)
          );

        if (!alreadyAdded) {
          group.courses.push(course);
        }
      });
    });

    const groups = Array.from(
      groupMap.values()
    ).sort(
      (first, second) =>
        GROUP_ORDER.indexOf(first.code) -
        GROUP_ORDER.indexOf(second.code)
    );

    const offerMap = new Map(
      bundles.map((bundle) => [
        bundle.offer_code,
        {
          type: "offer",
          code: bundle.offer_code,
          name: bundle.offer_name,
          price: Number(
            bundle.offer_price || 0
          ),
          originalPrice: Number(
            bundle.original_price || 0
          ),
          courses: Array.isArray(
            bundle.courses
          )
            ? Array.from(
                new Map(
                  bundle.courses.map(
                    (course) => [
                      Number(course.id),
                      course,
                    ]
                  )
                ).values()
              )
            : [],
        },
      ])
    );

    return {
      bundle1First: groups.find(
        (group) =>
          group.code === "BUNDLE1-A"
      ),

      bundle1Second: groups.find(
        (group) =>
          group.code === "BUNDLE1-B"
      ),

      bundle3Individual: groups.find(
        (group) =>
          group.code === "BUNDLE3"
      ),

      bundle2Individual: groups.find(
        (group) =>
          group.code === "BUNDLE2"
      ),

      bundle1Offer:
        offerMap.get("BUNDLE-1"),

      bundle23Offer:
        offerMap.get("BUNDLE-2-3"),

      overallOffer:
        offerMap.get("OVERALL-BUNDLE"),
    };
  }, [bundles]);

  /*
   * Submit the hidden form only after React
   * has rendered the selected payment option.
   */
  useEffect(() => {
    if (payOption && formRef.current) {
      formRef.current.submit();
    }
  }, [payOption]);

  /*
   * Reset payment selection when modal closes.
   */
  useEffect(() => {
    if (!isOpen) {
      setPayOption(null);
    }
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  const formatPrice = (value) =>
    Number(value || 0).toLocaleString(
      "en-IN",
      {
        maximumFractionDigits: 0,
      }
    );

  const stripBundleLabels = (
    text = ""
  ) =>
    text
      .replace(/Bundle\s*\d+/gi, "")
      .replace(
        /^[\s\-–+]+|[\s\-–+]+$/g,
        ""
      )
      .replace(/\s{2,}/g, " ");

  const handleOverlayClick = (event) => {
    if (
      event.target === event.currentTarget
    ) {
      onClose();
    }
  };

  const handlePayNow = (option) => {
    if (
      !option ||
      !Array.isArray(option.courses) ||
      option.courses.length === 0
    ) {
      return;
    }

    if (!user?.id) {
      window.alert(
        "Please log in before purchasing a bundle."
      );

      return;
    }

    /*
     * The option includes:
     *
     * option.type:
     * individual or offer
     *
     * option.code:
     * BUNDLE1-A
     * BUNDLE1-B
     * BUNDLE2
     * BUNDLE3
     * BUNDLE-1
     * BUNDLE-2-3
     * OVERALL-BUNDLE
     */
    setPayOption(option);
  };

  const renderCourseNames = (
    courses = [],
    limit = courses.length
  ) => (
    <ul className="purchase-course-list">
      {courses
        .slice(0, limit)
        .map((course, index) => (
          <li key={course.id}>
            <span className="purchase-check">
              {index + 1}
            </span>

            <span>
              {course.course_name}
            </span>
          </li>
        ))}

      {courses.length > limit && (
        <li className="purchase-more">
          +{courses.length - limit} more
          courses
        </li>
      )}
    </ul>
  );

  const IndividualCard = ({ option }) => {
    if (!option) {
      return null;
    }

    return (
      <article className="individual-purchase-card">
        <div className="purchase-card-copy">
          <div className="purchase-card-meta">
            <span className="purchase-tag">
              Individual Bundle
            </span>

            <span className="purchase-count">
              {option.courses.length} Courses
            </span>
          </div>

          {renderCourseNames(
            option.courses
          )}
        </div>

        <div className="purchase-card-action">
          <strong>
            ₹{formatPrice(option.price)}
          </strong>

          <button
            type="button"
            onClick={() =>
              handlePayNow(option)
            }
          >
            Pay Now
          </button>
        </div>
      </article>
    );
  };

  const CombinedCard = ({
    option,
    label,
  }) => {
    if (!option) {
      return null;
    }

    const saving =
      option.originalPrice > option.price
        ? option.originalPrice -
          option.price
        : 0;

    return (
      <article className="combined-purchase-card">
        <span className="combined-label">
          {label}
        </span>

        <p>
          {option.courses.length} courses
          included
        </p>

        <div className="combined-price">
          <strong>
            ₹{formatPrice(option.price)}
          </strong>

          {option.originalPrice >
            option.price && (
            <span>
              ₹
              {formatPrice(
                option.originalPrice
              )}
            </span>
          )}
        </div>

        {saving > 0 && (
          <div className="combined-saving">
            Save ₹{formatPrice(saving)}
          </div>
        )}

        <button
          type="button"
          onClick={() =>
            handlePayNow(option)
          }
        >
          Pay Now
        </button>
      </article>
    );
  };

  const overall = data.overallOffer;

  const overallSaving =
    overall &&
    overall.originalPrice > overall.price
      ? overall.originalPrice -
        overall.price
      : 0;

  return (
    <div
      className="bundle-modal-overlay"
      onMouseDown={handleOverlayClick}
    >
      <div
        className="bundle-map-modal"
        role="dialog"
        aria-modal="true"
        aria-label="Course bundle purchase options"
      >
        <button
          type="button"
          className="bundle-map-close"
          onClick={onClose}
          aria-label="Close bundle offers"
        >
          &times;
        </button>

        <div className="bundle-map-body">
          {loading && (
            <div className="bundle-status">
              <span className="bundle-loader" />

              <p>
                Loading bundle offers...
              </p>
            </div>
          )}

          {!loading &&
            errorMessage && (
              <div className="bundle-status bundle-error">
                <p>{errorMessage}</p>

                <button
                  type="button"
                  onClick={() =>
                    setReloadKey(
                      (value) => value + 1
                    )
                  }
                >
                  Try Again
                </button>
              </div>
            )}

          {!loading &&
            !errorMessage && (
              <div className="bundle-purchase-map">
                <div className="bundle-left-column">
                  {[
                    {
                      key: "bundle1",

                      description:
                        "Purchase either ₹300 bundle or get both for ₹499.",

                      first:
                        data.bundle1First,

                      second:
                        data.bundle1Second,

                      offer:
                        data.bundle1Offer,
                    },
                    {
                      key: "bundle23",

                      description:
                        "Purchase ₹300 or ₹400 separately, or get both for ₹599.",

                      first:
                        data.bundle3Individual,

                      second:
                        data.bundle2Individual,

                      offer:
                        data.bundle23Offer,
                    },
                  ]
                    .filter(
                      (section) =>
                        section.first ||
                        section.second ||
                        section.offer
                    )
                    .map(
                      (
                        section,
                        index
                      ) => (
                        <section
                          key={
                            section.key
                          }
                          className="bundle-map-section"
                        >
                          <div className="bundle-section-title">
                            <span>
                              {index + 1}
                            </span>

                            <div>
                              <p>
                                {
                                  section.description
                                }
                              </p>
                            </div>
                          </div>

                          <div className="bundle-section-layout">
                            <div className="individual-stack">
                              <IndividualCard
                                option={
                                  section.first
                                }
                              />

                              <IndividualCard
                                option={
                                  section.second
                                }
                              />
                            </div>

                            <div className="flow-connector">
                              <span />
                            </div>

                            <CombinedCard
                              option={
                                section.offer
                              }
                              label="Combined Offer"
                            />
                          </div>
                        </section>
                      )
                    )}
                </div>

                <div className="overall-connector">
                  <span />
                </div>

                {overall && (
                  <aside className="overall-purchase-card">
                    <span className="overall-badge">
                      Best Value
                    </span>

                    <p>
                      OVERALL BUNDLE
                    </p>

                    <h3>
                      {stripBundleLabels(
                        overall.name
                      )}
                    </h3>

                    <div className="overall-course-count">
                      {
                        overall.courses
                          .length
                      }

                      <span>
                        Courses Included
                      </span>
                    </div>

                    <div className="overall-price">
                      ₹
                      {formatPrice(
                        overall.price
                      )}
                    </div>

                    {overall.originalPrice >
                      overall.price && (
                      <div className="overall-old-price">
                        Normal Price ₹
                        {formatPrice(
                          overall.originalPrice
                        )}
                      </div>
                    )}

                    {overallSaving > 0 && (
                      <div className="overall-saving">
                        Save ₹
                        {formatPrice(
                          overallSaving
                        )}
                      </div>
                    )}

                    <button
                      type="button"
                      onClick={() =>
                        handlePayNow(
                          overall
                        )
                      }
                    >
                      Pay Now
                    </button>
                  </aside>
                )}
              </div>
            )}
        </div>
      </div>

      {/*
        IMPORTANT:

        This form calls the separate bundle-payment API.

        It does not send course_ids[].
        It does not send the old course total.
        It does not send the amount from React.

        Laravel reads the official amount from:
        bundle_groups.single_price
        or
        bundle_offers.offer_price
      */}
      {payOption && (
        <form
          ref={formRef}
          action={`${API_BASE}/payment/bundle/pre-pay-process`}
          method="POST"
          style={{ display: "none" }}
        >
          <input
            type="hidden"
            name="user_id"
            value={user?.id || ""}
          />

          <input
            type="hidden"
            name="purchase_type"
            value={payOption.type || ""}
          />

          <input
            type="hidden"
            name="purchase_code"
            value={payOption.code || ""}
          />
        </form>
      )}
    </div>
  );
}